/**
 * Link Analysis Sentinel Component
 * 
 * AI-powered analysis for link analysis data including:
 * - Device connections (calls, SMS, browsing)
 * - Location patterns
 * - Phone number networks
 * - Digital footprint analysis
 * - Interactive graph/map visualizations
 */

import React, { useState, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  Stack,
} from '@mui/material';
import {
  Psychology as AskIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Lightbulb as PromptIcon,
  Hub as NetworkIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Language as BrowsingIcon,
  FilterAlt as FilterIcon,
  CenterFocusStrong as FocusIcon,
  Clear as ClearIcon,
  Map as MapIcon,
  Sms as SmsIcon,
  Apps as AppsIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { generateSentinelChatPDF } from '@signals/app-shell/components/pdf';
import { useTheme } from '@mui/material/styles';

interface LinkNode {
  id: string;
  type: 'device' | 'location' | 'phone' | 'url' | 'app' | 'website';
  label?: string;
  entity_id?: string;
  entity_type?: string;
  place_name?: string;
  place_type?: string;
  latitude?: number;
  longitude?: number;
  app_name?: string;
  package_name?: string;
}

interface LinkEdge {
  source: string | { id: string };
  target: string | { id: string };
  type?: string;
  connection_type?: string;
  timestamp?: string;
  duration?: number;
  weight?: number;
  // SMS specific fields
  sms_message?: string;
  sms_number?: string;
  comms_direction?: string;
  // Call specific fields
  call_logs_number?: string;
  call_logs_duration?: number;
  // App specific fields
  package_name?: string;
  total_time?: number;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actions?: GraphAction[];
}

// Graph/Map action types
export type GraphActionType = 
  | 'highlight_nodes'      // Highlight specific nodes
  | 'filter_entities'      // Filter to specific entities
  | 'expand_node'          // Expand a node's connections
  | 'zoom_to_node'         // Center view on a node
  | 'filter_connection_type' // Filter by connection type
  | 'show_on_map'          // Show locations on map
  | 'clear_highlights'     // Clear all highlights
  | 'reset_view'           // Reset graph view
  | 'change_date_range'    // Change the date filter
  | 'fetch_and_analyze'    // Fetch new data then analyze
  | 'toggle_flow_map'      // Enable/disable dwell traffic flow visualization
  | 'switch_tab'           // Switch to a specific tab (Map, Charts, Timeline, etc.)
  | 'select_nodes';        // Select nodes in the graph (for fan-out analysis)

export interface GraphAction {
  type: GraphActionType;
  nodeIds?: string[];
  connectionType?: string;
  dateRange?: { start: Date; end: Date };  // For date-based actions
  days?: number;  // Alternative: number of days to look back
  followUpAction?: GraphAction;  // Action to run after data loads
  label: string;
  icon?: React.ReactNode;
  autoExecute?: boolean;  // If true, execute automatically when response is received
  tabName?: string;  // For switch_tab action: 'map' | 'charts' | 'timeline' | 'nodes' | 'legend' | 'help'
  enabled?: boolean;  // For toggle actions like toggle_flow_map
}

interface LinkAnalysisSentinelProps {
  nodes: LinkNode[];
  links: LinkEdge[];
  selectedEntityIds?: string[];
  dateRange?: { start: Date | null; end: Date | null };
  connectionType?: string;
  onNodeSelect?: (nodeId: string) => void;
  onGraphAction?: (action: GraphAction) => void;
  onDateRangeChange?: (start: Date, end: Date) => void;  // Callback to change date range
  // Additional state for better AI context
  selectedNodesCount?: number;  // Number of nodes currently selected in the graph
  flowMapEnabled?: boolean;     // Whether dwell traffic flow is enabled
  visibleLocationsCount?: number; // Number of locations visible in current view
  sharedLocationsCount?: number;  // Number of shared locations (visited by 2+ selected entities)
  // NEW: Context for understanding filtered vs. full data
  isFilteredView?: boolean;     // True when graph is showing a filtered subset (selection active)
  totalNodesInDataset?: number; // Total nodes in the base dataset (before selection filtering)
  totalLinksInDataset?: number; // Total links in the base dataset (before selection filtering)
}

const LinkAnalysisSentinel: React.FC<LinkAnalysisSentinelProps> = ({
  nodes,
  links,
  selectedEntityIds = [],
  dateRange,
  connectionType = 'all',
  onNodeSelect,
  onGraphAction,
  onDateRangeChange: _onDateRangeChange,
  selectedNodesCount = 0,
  flowMapEnabled = false,
  visibleLocationsCount,
  sharedLocationsCount,
  isFilteredView = false,
  totalNodesInDataset,
  totalLinksInDataset,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;

  // Chat state
  const [isLoading, setIsLoading] = useState(false);
  const [question, setQuestion] = useState('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [isChatMode, setIsChatMode] = useState(false);
  const [highlightedNodes, setHighlightedNodes] = useState<Set<string>>(new Set());
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Prompt menu state
  const [promptMenuAnchor, setPromptMenuAnchor] = useState<null | HTMLElement>(null);
  const promptMenuOpen = Boolean(promptMenuAnchor);

  // Predefined prompts for link analysis (now with visualization actions)
  const predefinedPrompts = [
    { category: '❓ How to Use', icon: <FilterIcon fontSize="small" />, prompts: [
      { label: 'What can you help with?', prompt: 'What kind of analysis can you help me with on this link analysis page? Give me a brief overview of your capabilities.' },
      { label: 'How to highlight connections', prompt: 'How do I use the click-to-highlight feature? Explain how clicking a node highlights its connections and fades unrelated nodes.' },
      { label: 'How to find shared locations', prompt: 'How do I find locations that multiple students or entities have visited in common? Explain step by step.' },
      { label: 'How to filter connections', prompt: 'How do I filter the graph to show only certain types of connections like calls or SMS? What are all the filtering options available?' },
      { label: 'How to use selection mode', prompt: 'How do I select multiple nodes and what does the fan-out feature do when I select a location?' },
      { label: 'Explain the map features', prompt: 'What features does the map tab offer? How do I use dwell traffic flow and what do the different markers mean?' },
      { label: 'Understanding the charts', prompt: 'What does the Charts tab show? Explain the shared connections matrix and other visualizations.' },
      { label: 'How to use Radial View', prompt: 'How do I use the Radial graph view? Explain Discovery Mode and Investigation Mode, and what the depth levels mean.' },
    ]},
    { category: '🎯 Radial & Depth Analysis', icon: <FocusIcon fontSize="small" />, prompts: [
      { label: 'Who has deepest network?', prompt: 'Which entities have the most Level 3 and Level 4 connections? Who has the deepest network reach?' },
      { label: 'Most connected at L2', prompt: 'Which entities have the most Level 2 connections (friends of friends)? Show me the top 5.' },
      { label: 'Network hubs', prompt: 'Identify network hubs - entities that connect many otherwise disconnected groups. Who are the bridge nodes?' },
      { label: 'Connection chain analysis', prompt: 'Show me the connection chain between the two most distant entities in the network. How many hops apart are they?' },
      { label: 'Depth comparison', prompt: 'Compare the network depth of all students. Who has the widest reach at each level (L1, L2, L3, L4)?' },
      { label: 'Isolated vs connected', prompt: 'Which entities are most isolated (few connections) vs most connected? Show the contrast.' },
      { label: 'Recommend investigation target', prompt: 'Based on connection patterns, which entity would you recommend I investigate first using the radial view? Why?' },
    ]},
    { category: 'Network Overview', icon: <NetworkIcon fontSize="small" />, prompts: [
      { label: 'Network Summary', prompt: 'Give me a summary of the connection network and highlight the key nodes' },
      { label: 'Most Connected Nodes', prompt: 'Show me the most connected entities and highlight them on the graph' },
      { label: 'Connection Clusters', prompt: 'Find and highlight distinct clusters in the network' },
    ]},
    { category: 'Communication Analysis', icon: <PhoneIcon fontSize="small" />, prompts: [
      { label: 'Call Patterns', prompt: 'Analyze call patterns and show the most active callers' },
      { label: 'Top Phone Numbers', prompt: 'Highlight the most frequently contacted phone numbers' },
      { label: 'SMS Network', prompt: 'Filter to show only SMS connections' },
    ]},
    { category: 'SMS Conversations', icon: <SmsIcon fontSize="small" />, prompts: [
      { label: 'Summarize All SMS', prompt: 'Summarize all SMS conversations in this network' },
      { label: 'SMS Between Entities', prompt: 'Show me the SMS conversation between the most connected entities' },
      { label: 'SMS Topics', prompt: 'What topics are being discussed in the SMS messages?' },
      { label: 'SMS Timeline', prompt: 'Give me a timeline of SMS activity and key messages' },
    ]},
    { category: 'Location Intelligence', icon: <LocationIcon fontSize="small" />, prompts: [
      { label: 'Shared Locations', prompt: 'Find locations shared by multiple entities and show them on the map' },
      { label: 'Show All Locations', prompt: 'Display all locations on the map' },
      { label: 'Frequent Locations', prompt: 'Highlight the most frequently visited locations' },
      { label: 'Enable Traffic Flow', prompt: 'Enable the dwell traffic flow visualization to see movement patterns between locations' },
      { label: 'Movement Patterns', prompt: 'Analyze movement patterns - where do entities travel between most frequently?' },
    ]},
    { category: 'Digital Footprint', icon: <BrowsingIcon fontSize="small" />, prompts: [
      { label: 'Browsing Patterns', prompt: 'Analyze browsing patterns and highlight common sites' },
      { label: 'Filter to Browsing', prompt: 'Filter to show only browsing history connections' },
      { label: 'Digital Overlap', prompt: 'Find and highlight entities with overlapping digital footprints' },
    ]},
    { category: 'App Usage', icon: <AppsIcon fontSize="small" />, prompts: [
      { label: 'Top Apps', prompt: 'Show me the most used apps and highlight entities using them' },
      { label: 'Filter to Apps', prompt: 'Filter to show only app usage connections' },
      { label: 'App Patterns', prompt: 'Analyze app usage patterns across all entities' },
      { label: 'Shared Apps', prompt: 'Find apps that multiple entities have in common' },
      { label: 'App Usage Timeline', prompt: 'Show a timeline of app activity across entities' },
    ]},
  ];

  // Theme colors - transparent background to match main frame
  const colors = useMemo(() => ({
    background: 'transparent',
    cardBg: isDark ? 'rgba(40, 44, 62, 0.4)' : 'rgba(248, 250, 252, 0.9)',
    border: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
    text: isDark ? '#ffffff' : '#1a1a2e',
    textSecondary: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
    textTertiary: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
    accent: primaryColor,
    accentRgb: '59, 130, 246',
  }), [isDark, primaryColor]);

  // Analyze network data
  const networkStats = useMemo(() => {
    const nodeTypes: Record<string, number> = {};
    const connectionTypes: Record<string, number> = {};
    const nodeConnections: Record<string, number> = {};

    // Count node types
    nodes.forEach(node => {
      nodeTypes[node.type] = (nodeTypes[node.type] || 0) + 1;
    });

    // Count connection types and node connections
    links.forEach(link => {
      const type = link.connection_type || link.type || 'unknown';
      connectionTypes[type] = (connectionTypes[type] || 0) + 1;

      const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target?.id;

      if (sourceId) nodeConnections[sourceId] = (nodeConnections[sourceId] || 0) + 1;
      if (targetId) nodeConnections[targetId] = (nodeConnections[targetId] || 0) + 1;
    });

    // Find most connected nodes
    const sortedNodes = Object.entries(nodeConnections)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);

    // Find devices and their labels
    const deviceNodes = nodes.filter(n => n.type === 'device');
    const locationNodes = nodes.filter(n => n.type === 'location' || n.type === 'dwell');
    const phoneNodes = nodes.filter(n => n.type === 'phone');
    const appNodes = nodes.filter(n => n.type === 'app');
    const websiteNodes = nodes.filter(n => n.type === 'website');

    return {
      totalNodes: nodes.length,
      totalLinks: links.length,
      nodeTypes,
      connectionTypes,
      topConnected: sortedNodes,
      deviceCount: deviceNodes.length,
      locationCount: locationNodes.length,
      phoneCount: phoneNodes.length,
      appCount: appNodes.length,
      websiteCount: websiteNodes.length,
      avgConnections: nodes.length > 0 ? (links.length * 2 / nodes.length).toFixed(1) : '0',
    };
  }, [nodes, links]);

  // Parse AI response for actionable entities
  const parseActionsFromResponse = useCallback((response: string, query: string): GraphAction[] => {
    const actions: GraphAction[] = [];
    const q = query.toLowerCase();

    // AUTO-FILTER: Detect query type and automatically filter the graph
    // SMS-related queries
    const smsKeywords = ['sms', 'text message', 'message conversation', 'summarize sms', 'sms conversation', 'texting', 'text log'];
    const isSmsQuery = smsKeywords.some(kw => q.includes(kw));
    
    // Call-related queries
    const callKeywords = ['call', 'phone call', 'call log', 'calling', 'called', 'call pattern', 'call history'];
    const isCallQuery = callKeywords.some(kw => q.includes(kw)) && !q.includes('text');
    
    // Browsing-related queries
    const browsingKeywords = ['browsing', 'browser', 'website', 'web history', 'internet', 'digital footprint'];
    const isBrowsingQuery = browsingKeywords.some(kw => q.includes(kw));
    
    // Dwell/location-related queries
    const dwellKeywords = ['dwell', 'visited', 'stayed at', 'location visit'];
    const isDwellQuery = dwellKeywords.some(kw => q.includes(kw));
    
    // App usage-related queries
    const appKeywords = ['app', 'apps', 'app usage', 'application', 'installed app', 'mobile app', 'package', 'using app'];
    const isAppQuery = appKeywords.some(kw => q.includes(kw));

    // Add auto-execute filter based on query type
    if (isSmsQuery) {
      actions.push({
        type: 'filter_connection_type',
        connectionType: 'sms',
        label: 'Filtering to SMS connections',
        icon: <SmsIcon fontSize="small" />,
        autoExecute: true,
      });
    } else if (isCallQuery) {
      actions.push({
        type: 'filter_connection_type',
        connectionType: 'call',
        label: 'Filtering to call connections',
        icon: <PhoneIcon fontSize="small" />,
        autoExecute: true,
      });
    } else if (isBrowsingQuery) {
      actions.push({
        type: 'filter_connection_type',
        connectionType: 'browsing',
        label: 'Filtering to browsing connections',
        icon: <BrowsingIcon fontSize="small" />,
        autoExecute: true,
      });
    } else if (isDwellQuery) {
      actions.push({
        type: 'filter_connection_type',
        connectionType: 'dwell',
        label: 'Filtering to dwell connections',
        icon: <LocationIcon fontSize="small" />,
        autoExecute: true,
      });
    } else if (isAppQuery) {
      actions.push({
        type: 'filter_connection_type',
        connectionType: 'app',
        label: 'Filtering to app usage connections',
        icon: <AppsIcon fontSize="small" />,
        autoExecute: true,
      });
    }

    // Detect "show on map" intent
    if (q.includes('map') || q.includes('location')) {
      const locationNodes = nodes.filter(n => n.type === 'location' && n.latitude && n.longitude);
      if (locationNodes.length > 0) {
        actions.push({
          type: 'show_on_map',
          nodeIds: locationNodes.slice(0, 20).map(n => n.id),
          label: `Show ${Math.min(locationNodes.length, 20)} locations on map`,
          icon: <MapIcon fontSize="small" />,
        });
      }
    }

    // Detect "most connected" intent - AUTO-FILTER
    const isHighlightQuery = q.includes('highlight') || q.includes('show me') || q.includes('find');
    if (q.includes('most connected') || q.includes('key nodes') || q.includes('top')) {
      const topNodes = networkStats.topConnected.slice(0, 5).map(([id]) => id);
      if (topNodes.length > 0) {
        actions.push({
          type: 'filter_entities',
          nodeIds: topNodes,
          label: `Filtering to top ${topNodes.length} connected nodes`,
          icon: <FilterIcon fontSize="small" />,
          autoExecute: isHighlightQuery, // Auto-execute if query asks to highlight/show
        });
      }
    }

    // Detect "highlight ALL" entity/device/node type queries
    if (isHighlightQuery && (q.includes('all entity') || q.includes('all entities') || q.includes('all device') || q.includes('all devices') || q.includes('all node'))) {
      // Get all device type nodes (devices are the "entity" nodes in the graph)
      const entityNodes = nodes.filter(n => n.type === 'device');
      if (entityNodes.length > 0) {
        actions.push({
          type: 'highlight_nodes',
          nodeIds: entityNodes.map(n => n.id),
          label: `✨ Highlight all ${entityNodes.length} entities`,
          icon: <FocusIcon fontSize="small" />,
          autoExecute: true,
        });
      }
    }
    
    // Detect "highlight all phones" query
    if (isHighlightQuery && (q.includes('all phone') || q.includes('all number'))) {
      const phoneNodes = nodes.filter(n => n.type === 'phone');
      if (phoneNodes.length > 0) {
        actions.push({
          type: 'highlight_nodes',
          nodeIds: phoneNodes.map(n => n.id),
          label: `✨ Highlight all ${phoneNodes.length} phone numbers`,
          icon: <FocusIcon fontSize="small" />,
          autoExecute: true,
        });
      }
    }
    
    // Detect "highlight all locations" query
    if (isHighlightQuery && (q.includes('all location') || q.includes('all place'))) {
      const locationNodes = nodes.filter(n => n.type === 'location');
      if (locationNodes.length > 0) {
        actions.push({
          type: 'highlight_nodes',
          nodeIds: locationNodes.map(n => n.id),
          label: `✨ Highlight all ${locationNodes.length} locations`,
          icon: <FocusIcon fontSize="small" />,
          autoExecute: true,
        });
      }
    }
    
    // Detect "highlight all websites" query
    if (isHighlightQuery && (q.includes('all website') || q.includes('all domain') || q.includes('all site'))) {
      const websiteNodes = nodes.filter(n => n.type === 'website');
      if (websiteNodes.length > 0) {
        actions.push({
          type: 'highlight_nodes',
          nodeIds: websiteNodes.map(n => n.id),
          label: `✨ Highlight all ${websiteNodes.length} websites`,
          icon: <FocusIcon fontSize="small" />,
          autoExecute: true,
        });
      }
    }

    // === ROBUST ENTITY EXTRACTION ===
    // Helper function to find a node by name (case-insensitive, flexible matching)
    const findNodeByName = (name: string): typeof nodes[0] | undefined => {
      const nameLower = name.toLowerCase().trim();
      return nodes.find(n => 
        n.id.toLowerCase() === nameLower || 
        n.label?.toLowerCase() === nameLower ||
        n.id.toLowerCase().includes(nameLower) ||
        n.label?.toLowerCase().includes(nameLower)
      );
    };
    
    // Extract entity names from query using multiple patterns
    const extractEntityNames = (query: string): string[] => {
      const entities: string[] = [];
      
      // Normalize query - remove extra spaces around hyphens/underscores
      const normalizedQuery = query.replace(/\s*[-_]\s*/g, '-');
      
      // Pattern 1: Common device naming patterns (Student-01, FSR-01, RP-17, QP1-test, etc.)
      const devicePattern = /\b([a-zA-Z]{2,}[-_]?\d+[-_]?[a-zA-Z]*\d*)\b/gi;
      const deviceMatches = normalizedQuery.match(devicePattern);
      if (deviceMatches) entities.push(...deviceMatches);
      
      // Pattern 2: Also check original query for "word number" patterns like "student 03"
      const spacedPattern = /\b(student|fsr|rp|qp|trainee|device)\s*(\d+)\b/gi;
      let spacedMatch;
      while ((spacedMatch = spacedPattern.exec(query)) !== null) {
        entities.push(`${spacedMatch[1]}-${spacedMatch[2]}`);
      }
      
      // Pattern 3: Quoted names "Entity Name"
      const quotedPattern = /"([^"]+)"|'([^']+)'/g;
      let quotedMatch;
      while ((quotedMatch = quotedPattern.exec(query)) !== null) {
        entities.push(quotedMatch[1] || quotedMatch[2]);
      }
      
      return Array.from(new Set(entities)); // Remove duplicates
    };
    
    const mentionedEntities = extractEntityNames(q);
    const validNodes = mentionedEntities
      .map(name => findNodeByName(name))
      .filter((n): n is typeof nodes[0] => n !== undefined);
    
    // Debug logging for action detection
    if (mentionedEntities.length > 0) {
      console.log('[Sentinel] Extracted entities from query:', mentionedEntities);
      console.log('[Sentinel] Valid nodes found:', validNodes.map(n => n.id));
    }
    
    // === ZOOM TO / PAN TO commands ===
    if (q.match(/\b(zoom\s*(to|in\s*on)|pan\s*to|center\s*on|focus\s*on|go\s*to)\b/i) && validNodes.length > 0) {
      const targetNode = validNodes[0];
      actions.push({
        type: 'zoom_to_node',
        nodeIds: [targetNode.id],
        label: `🎯 Zoom to ${targetNode.label || targetNode.id}`,
        icon: <FocusIcon fontSize="small" />,
        autoExecute: true,
      });
    }
    
    // === HIGHLIGHT specific entity commands ===
    if (q.match(/\bhighlight\b/i) && validNodes.length > 0 && !q.includes('all ')) {
      actions.push({
        type: 'highlight_nodes',
        nodeIds: validNodes.map(n => n.id),
        label: `✨ Highlight ${validNodes.map(n => n.label || n.id).join(', ')}`,
        icon: <FocusIcon fontSize="small" />,
        autoExecute: true,
      });
    }
    
    // === FILTER TO specific entity commands ===
    if (q.match(/\b(filter\s*(to|graph\s*to)|show\s*only|isolate|focus\s*graph)\b/i) && validNodes.length > 0) {
      // Filter to show only the mentioned entities and their connections
      actions.push({
        type: 'filter_entities',
        nodeIds: validNodes.map(n => n.id),
        label: `🔍 Filter to ${validNodes.map(n => n.label || n.id).join(', ')} activity`,
        icon: <FilterIcon fontSize="small" />,
        autoExecute: true,
      });
    }
    
    // === EXPAND specific entity commands ===
    if (q.match(/\b(expand|show\s*connections\s*(for|of)|show\s*all\s*connections)\b/i) && validNodes.length > 0) {
      const targetNode = validNodes[0];
      actions.push({
        type: 'expand_node',
        nodeIds: [targetNode.id],
        label: `🌐 Expand ${targetNode.label || targetNode.id} connections`,
        icon: <NetworkIcon fontSize="small" />,
        autoExecute: true,
      });
    }
    
    // === Legacy highlight detection (fallback) ===
    const highlightMatch = q.match(/highlight|show|find|focus/);
    if (highlightMatch && validNodes.length === 0) {
      // Check for device mentions with simpler pattern as fallback
      const devicePattern = /([a-zA-Z]+-\d+|student-\d+|trainee-\d+)/gi;
      const mentionedDevices = q.match(devicePattern);
      if (mentionedDevices) {
        const nodeIds = mentionedDevices.filter(id => 
          nodes.some(n => n.id.toLowerCase() === id.toLowerCase() || n.label?.toLowerCase() === id.toLowerCase())
        );
        if (nodeIds.length > 0) {
          actions.push({
            type: 'highlight_nodes',
            nodeIds,
            label: `🎯 Highlight ${nodeIds.join(', ')}`,
            icon: <FocusIcon fontSize="small" />,
          });
        }
      }
    }

    // Detect "filter" by connection type
    const filterTypes = ['call', 'sms', 'browsing', 'app', 'dwell'];
    for (const filterType of filterTypes) {
      if (q.includes(`filter to ${filterType}`) || q.includes(`show only ${filterType}`) || q.includes(`${filterType} connection`)) {
        actions.push({
          type: 'filter_connection_type',
          connectionType: filterType,
          label: `🔗 Filter to ${filterType} connections`,
          icon: <FilterIcon fontSize="small" />,
        });
        break;
      }
    }

    // Detect "cluster" or "group" intent - AUTO-FILTER
    if (q.includes('cluster') || q.includes('group')) {
      // Find nodes that are highly interconnected
      const clusters = findClusters();
      if (clusters.length > 0) {
        actions.push({
          type: 'filter_entities',
          nodeIds: clusters[0], // Filter to first cluster
          label: `🌐 Filtering to largest cluster (${clusters[0].length} nodes)`,
          icon: <FilterIcon fontSize="small" />,
          autoExecute: isHighlightQuery, // Auto-execute if query asks to highlight/find
        });
      }
    }

    // Detect time-based queries (e.g., "last 30 days", "past week", "last month")
    const timePatterns = [
      { pattern: /last\s+(\d+)\s+days?/i, unit: 'days' },
      { pattern: /past\s+(\d+)\s+days?/i, unit: 'days' },
      { pattern: /(\d+)\s+days?\s+ago/i, unit: 'days' },
      { pattern: /last\s+(\d+)\s+weeks?/i, unit: 'weeks' },
      { pattern: /past\s+(\d+)\s+weeks?/i, unit: 'weeks' },
      { pattern: /last\s+(\d+)\s+months?/i, unit: 'months' },
      { pattern: /past\s+(\d+)\s+months?/i, unit: 'months' },
      { pattern: /last\s+week/i, unit: 'week' },
      { pattern: /past\s+week/i, unit: 'week' },
      { pattern: /last\s+month/i, unit: 'month' },
      { pattern: /past\s+month/i, unit: 'month' },
    ];

    let requestedDays: number | null = null;
    for (const { pattern, unit } of timePatterns) {
      const match = q.match(pattern);
      if (match) {
        const value = match[1] ? parseInt(match[1]) : 1;
        switch (unit) {
          case 'days':
            requestedDays = value;
            break;
          case 'weeks':
          case 'week':
            requestedDays = value * 7;
            break;
          case 'months':
          case 'month':
            requestedDays = value * 30;
            break;
        }
        break;
      }
    }

    // Check if the requested time range is different from current
    if (requestedDays && dateRange) {
      const currentDays = dateRange.start && dateRange.end 
        ? Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24))
        : 7;
      
      if (requestedDays > currentDays) {
        // Need to fetch more data
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - requestedDays);
        
        actions.unshift({
          type: 'change_date_range',
          days: requestedDays,
          dateRange: { start, end },
          label: `Load last ${requestedDays} days of data`,
          icon: <FilterIcon fontSize="small" />,
          // After loading, highlight the top nodes
          followUpAction: {
            type: 'highlight_nodes',
            nodeIds: [], // Will be populated after data loads
            label: 'Highlight top connected',
          },
        });
      }
    }

    // Parse AI RESPONSE for mentioned entities and auto-filter/highlight them
    // This catches entity names the AI mentions in its analysis
    if (isHighlightQuery && response) {
      const foundNodeIds: string[] = [];
      
      // Search for each node's ID or label in the response (only device nodes, not locations)
      for (const node of nodes) {
        // Only match device-type nodes (not locations, phones, etc.) to avoid over-filtering
        if (node.type !== 'device') continue;
        
        const nodeId = node.id;
        const nodeLabel = node.label || '';
        
        // Check if node ID or label is mentioned in the response (case-insensitive)
        // Use word boundaries to avoid partial matches
        const idRegex = new RegExp(`\\b${nodeId.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'i');
        const labelRegex = nodeLabel ? new RegExp(`\\b${nodeLabel.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'i') : null;
        
        if (idRegex.test(response) || (labelRegex && labelRegex.test(response))) {
          foundNodeIds.push(nodeId);
        }
      }
      
      // If we found entities in the AI response, highlight them (not filter)
      if (foundNodeIds.length > 0 && foundNodeIds.length <= 50) {
        // Check if we already have a highlight action for these nodes
        const existingHighlight = actions.find(a => a.type === 'highlight_nodes');
        if (!existingHighlight) {
          // Add highlight action to visually emphasize mentioned entities
          actions.push({
            type: 'highlight_nodes',
            nodeIds: foundNodeIds,
            label: `✨ Highlighting ${foundNodeIds.length} mentioned entities`,
            icon: <FocusIcon fontSize="small" />,
            autoExecute: true,
          });
        }
      }
    }

    // Always offer clear/reset
    if (highlightedNodes.size > 0) {
      actions.push({
        type: 'clear_highlights',
        label: '🧹 Clear highlights',
        icon: <ClearIcon fontSize="small" />,
      });
    }

    return actions;
  }, [nodes, networkStats.topConnected, highlightedNodes.size]);

  // Simple cluster detection (find connected components)
  const findClusters = useCallback(() => {
    const visited = new Set<string>();
    const clusters: string[][] = [];

    const dfs = (nodeId: string, cluster: string[]) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);
      cluster.push(nodeId);

      links.forEach(link => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
        
        if (sourceId === nodeId && targetId) dfs(targetId, cluster);
        if (targetId === nodeId && sourceId) dfs(sourceId, cluster);
      });
    };

    nodes.forEach(node => {
      if (!visited.has(node.id)) {
        const cluster: string[] = [];
        dfs(node.id, cluster);
        if (cluster.length > 1) {
          clusters.push(cluster);
        }
      }
    });

    return clusters.sort((a, b) => b.length - a.length);
  }, [nodes, links]);

  // Execute a graph action
  const executeAction = useCallback((action: GraphAction) => {
    console.log('[LinkAnalysisSentinel] Executing action:', action);
    
    if (action.type === 'highlight_nodes' && action.nodeIds) {
      setHighlightedNodes(new Set(action.nodeIds));
    } else if (action.type === 'clear_highlights') {
      setHighlightedNodes(new Set());
    }
    
    if (onGraphAction) {
      onGraphAction(action);
    }
  }, [onGraphAction]);

  // Build context for AI (include available actions)
  const buildContext = () => {
    // Build explicit network overview for AI
    const networkOverview = {
      summary: isFilteredView 
        ? `⚠️ FILTERED VIEW: The user has ${selectedNodesCount} nodes selected. The graph/map is now showing only ${nodes.length} nodes and ${links.length} links (a subset of the full ${totalNodesInDataset || '?'} nodes and ${totalLinksInDataset || '?'} links). ALWAYS refer to the filtered counts when answering questions about the current view.`
        : `Full dataset view: ${nodes.length} nodes and ${links.length} connections visible. No selection filter active.`,
      // CURRENT VIEW (what user sees RIGHT NOW)
      currentViewCounts: {
        nodes: nodes.length,
        links: links.length,
        isFiltered: isFilteredView,
      },
      // FULL DATASET (for reference)
      fullDatasetCounts: isFilteredView ? {
        nodes: totalNodesInDataset || nodes.length,
        links: totalLinksInDataset || links.length,
      } : null,
      nodeBreakdown: {
        devices: networkStats.deviceCount,
        locations: networkStats.locationCount,
        phoneNumbers: networkStats.phoneCount,
        apps: networkStats.appCount,
        websites: networkStats.websiteCount,
        total: nodes.length,
      },
      connectionBreakdown: networkStats.connectionTypes,
      topConnectedNodes: networkStats.topConnected.slice(0, 5).map(([id, count]) => {
        const node = nodes.find(n => n.id === id);
        return { id, label: node?.label || id, type: node?.type, connections: count };
      }),
      currentFilters: {
        connectionType: connectionType,
        dateRange: dateRange ? {
          start: dateRange.start?.toISOString(),
          end: dateRange.end?.toISOString(),
        } : 'all time',
        selectedEntities: selectedEntityIds.length > 0 ? selectedEntityIds : 'none',
      },
      // Current view state
      currentViewState: {
        selectedNodesCount: selectedNodesCount,
        isFiltered: isFilteredView,
        filterNote: isFilteredView 
          ? `⚠️ CRITICAL: Answer questions about the CURRENT VIEW which shows ${nodes.length} nodes and ${links.length} links. Do NOT use full dataset counts.`
          : 'Showing full dataset (no selection active)',
        flowMapEnabled: flowMapEnabled,
        visibleLocationsInMap: visibleLocationsCount ?? networkStats.locationCount,
        sharedLocations: sharedLocationsCount ?? 0,
        mapNote: flowMapEnabled 
          ? 'Dwell Traffic Flow is ENABLED - map shows movement patterns between locations'
          : 'Standard map view - shows location markers for selected entities',
      },
    };

    const deviceDetails = nodes
      .filter(n => n.type === 'device')
      .slice(0, 20)
      .map(n => ({
        id: n.id,
        label: n.label || n.id,
        entityType: n.entity_type,
        connections: links.filter(l => {
          const sid = typeof l.source === 'string' ? l.source : l.source?.id;
          const tid = typeof l.target === 'string' ? l.target : l.target?.id;
          return sid === n.id || tid === n.id;
        }).length,
      }));

    const locationDetails = nodes
      .filter(n => n.type === 'location')
      .slice(0, 20)
      .map(n => ({
        id: n.id,
        name: n.place_name || n.label || n.id,
        type: n.place_type,
        lat: n.latitude,
        lon: n.longitude,
      }));

    const phoneDetails = nodes
      .filter(n => n.type === 'phone')
      .slice(0, 20)
      .map(n => ({
        id: n.id,
        number: n.label || n.id,
      }));

    const connectionSamples = links.slice(0, 50).map(l => {
      const sid = typeof l.source === 'string' ? l.source : l.source?.id;
      const tid = typeof l.target === 'string' ? l.target : l.target?.id;
      return {
        source: sid,
        target: tid,
        type: l.connection_type || l.type,
        timestamp: l.timestamp,
      };
    });

    // Extract SMS conversations for AI analysis
    const smsMessages = links
      .filter(l => (l.connection_type === 'sms' || l.type === 'sms') && l.sms_message)
      .slice(0, 100) // Limit to 100 messages
      .map(l => {
        const sid = typeof l.source === 'string' ? l.source : l.source?.id;
        const tid = typeof l.target === 'string' ? l.target : l.target?.id;
        return {
          from: sid,
          to: tid,
          direction: l.comms_direction || 'unknown',
          phoneNumber: l.sms_number,
          message: l.sms_message,
          timestamp: l.timestamp,
        };
      })
      .sort((a, b) => {
        if (!a.timestamp || !b.timestamp) return 0;
        return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      });

    // Group SMS by conversation pairs for easier analysis
    const smsConversations: Record<string, typeof smsMessages> = {};
    smsMessages.forEach(msg => {
      const pair = [msg.from, msg.to].sort().join('↔');
      if (!smsConversations[pair]) {
        smsConversations[pair] = [];
      }
      smsConversations[pair].push(msg);
    });

    // Extract call logs for AI analysis
    const callLogs = links
      .filter(l => (l.connection_type === 'call' || l.type === 'call'))
      .slice(0, 50)
      .map(l => {
        const sid = typeof l.source === 'string' ? l.source : l.source?.id;
        const tid = typeof l.target === 'string' ? l.target : l.target?.id;
        return {
          from: sid,
          to: tid,
          phoneNumber: l.call_logs_number,
          duration: l.call_logs_duration,
          timestamp: l.timestamp,
        };
      });

    // Extract app usage for AI analysis
    const appUsage = links
      .filter(l => (l.connection_type === 'app' || l.type === 'app'))
      .slice(0, 100)
      .map(l => {
        const sid = typeof l.source === 'string' ? l.source : l.source?.id;
        const tid = typeof l.target === 'string' ? l.target : l.target?.id;
        const appNode = nodes.find(n => n.id === tid && n.type === 'app');
        return {
          entity: sid,
          app: tid,
          appName: appNode?.app_name || appNode?.label || tid,
          packageName: appNode?.package_name,
          useCount: (l as any).weight || 1,
          totalTime: (l as any).total_time || 0,
          timestamp: l.timestamp,
        };
      });

    // Group app usage by entity for easier analysis
    const appUsageByEntity: Record<string, typeof appUsage> = {};
    appUsage.forEach(usage => {
      if (!appUsageByEntity[usage.entity]) {
        appUsageByEntity[usage.entity] = [];
      }
      appUsageByEntity[usage.entity].push(usage);
    });

    // Get unique apps and their total usage
    const appStats: Record<string, { count: number; entities: string[] }> = {};
    appUsage.forEach(usage => {
      if (!appStats[usage.appName]) {
        appStats[usage.appName] = { count: 0, entities: [] };
      }
      appStats[usage.appName].count += usage.useCount;
      if (!appStats[usage.appName].entities.includes(usage.entity)) {
        appStats[usage.appName].entities.push(usage.entity);
      }
    });

    return {
      // IMPORTANT: Network Overview - This is the current state of the graph/map
      networkOverview,
      networkStats,
      deviceDetails,
      locationDetails,
      phoneDetails,
      connectionSamples,
      // SMS data for conversation analysis
      smsMessages,
      smsConversations,
      smsStats: {
        totalMessages: smsMessages.length,
        conversationCount: Object.keys(smsConversations).length,
        participantPairs: Object.keys(smsConversations),
      },
      // Call log data
      callLogs,
      callStats: {
        totalCalls: callLogs.length,
      },
      // App usage data
      appUsage,
      appUsageByEntity,
      appStats: {
        totalAppConnections: appUsage.length,
        uniqueApps: Object.keys(appStats).length,
        topApps: Object.entries(appStats)
          .sort((a, b) => b[1].count - a[1].count)
          .slice(0, 10)
          .map(([name, stats]) => ({ name, ...stats })),
      },
      selectedEntities: selectedEntityIds,
      dateRange: dateRange ? {
        start: dateRange.start?.toISOString(),
        end: dateRange.end?.toISOString(),
      } : null,
      connectionTypeFilter: connectionType,
      // Tell AI it can suggest actions
      availableActions: [
        'highlight_nodes - Highlight specific nodes in the graph',
        'show_on_map - Display locations on the integrated map',
        'filter_connection_type - Filter to specific connection types (call, sms, browsing, app, dwell)',
        'filter_entities - Filter to specific entity/entities for investigation',
        'expand_node - Show all connections for a specific node',
        'zoom_to_node - Center the view on a specific node',
        'summarize_sms - Summarize SMS conversations between entities',
        'show_app_usage - Show app usage patterns and connections',
        'toggle_flow_map - Enable/disable dwell traffic flow visualization on the map',
        'switch_tab - Switch to a specific tab (map, charts, timeline, nodes, legend, help)',
      ],
      // Radial Graph & Depth Analysis capabilities
      radialGraphFeatures: {
        description: 'The Radial View shows entities in concentric rings based on hop distance from a seed node',
        modes: {
          discovery: 'When no entity is selected, shows top 50 entities ranked by total connections with L1-L4 depth breakdown',
          investigation: 'When an entity is selected, shows it at center with connections in rings (L1=direct, L2=2 hops, etc.)',
        },
        depthLevels: {
          L1: 'Level 1 - Direct connections (friends, direct contacts)',
          L2: 'Level 2 - Friends of friends, 2 hops away',
          L3: 'Level 3 - 3 hops away, extended network',
          L4: 'Level 4 - 4 hops away, maximum depth',
        },
        tips: [
          'Entities with high L3/L4 counts have wide network reach',
          'Bridge nodes connect otherwise disconnected groups',
          'Use depth filter to focus on immediate vs extended connections',
        ],
      },
      // Click-to-Highlight Feature (works in both Network and Radial views)
      clickToHighlight: {
        description: 'Click any node to highlight its connections and fade unrelated nodes for easier analysis',
        usage: {
          click: 'Click a node to highlight it and all its direct connections - other nodes fade to ~10% opacity',
          clear: 'Click the same node again, click empty space, or press Escape to clear the highlight',
          info: 'A cyan info panel appears showing the focused node name and connection count',
        },
        benefits: [
          'Instantly see who/what a node connects to in a dense graph',
          'Click a phone number to see which entities contacted it',
          'Click a location to see which entities visited it',
          'Works in both Network view and Radial view',
        ],
      },
      // Behavioral Embeddings & Similarity Search (pgvector powered) - for AI use
      embeddingsFeatures: {
        description: 'The system uses three types of embeddings: entity behavioral (64-dim), trajectory (64-dim), and dwell location (64-dim). All use cosine similarity via pgvector.',
        entityEmbeddings: {
          dimensions: 128,
          description: 'Entity behavioral embeddings capture comprehensive activity patterns.',
          similarityMetric: 'Cosine similarity - 0.0 (different) to 1.0 (identical)',
        },
        trajectoryEmbeddings: {
          dimensions: 128,
          description: 'Location sequences (movement paths) embedded to find similar travel patterns.',
          useCase: 'Find entities who travel similar routes even if specific locations differ',
        },
        dwellLocationEmbeddings: {
          dimensions: 64,
          description: 'Dwell location embeddings capture behavioral patterns of PLACES, not entities.',
          useCase: 'Find similar locations - coffee shops visited at similar times by similar people',
        },
        api: {
          similarEntities: 'GET /pattern-of-life/similar-entities/{entity_id}',
          similarTrajectories: 'GET /pattern-of-life/similar-trajectories/{sequence_id}',
          similarLocations: 'GET /pattern-of-life/similar-locations/{dwell_id}',
          anomalyDetection: 'GET /pattern-of-life/anomaly-detection/{entity_id}',
        },
      },
      // IMPORTANT: Instructions for the AI
      instructions: `When answering questions about the network:
        - The "locations" count refers to location nodes in the graph (places visited by entities)
        - The "devices" count refers to entity nodes (students, trainees, devices being tracked)
        - The "phoneNumbers" count refers to external phone numbers entities communicated with
        - The "apps" count refers to applications used by entities
        - Connection types include: call, sms, dwell (location visits), browsing, app
        - Always use the actual counts from networkOverview.nodeBreakdown when asked about quantities
        - The map tab shows the same locations as the graph, filtered by current selection
        - For depth/radial analysis: L1=direct connections, L2=2 hops (friends of friends), L3=3 hops, L4=4 hops
        - When asked about network depth or radial view, calculate BFS distances from each entity to count L1-L4 connections
        - Recommend entities with high L3/L4 counts for investigation as they have wide network influence
        - CLICK-TO-HIGHLIGHT: Users can click any node to highlight its connections and fade unrelated nodes. This works in both Network and Radial views. Click same node or press Escape to clear. Great for tracing connections in dense graphs.
        
        EMBEDDINGS & SIMILARITY (3 Types):
        1. ENTITY BEHAVIORAL (64-dim): Encodes who an entity IS based on their patterns
           - Created from: routine/mobility/social scores, co-location, co-travel, routes, digital, temporal
           - High similarity (>0.7) = entities behave very alike; moderate (0.4-0.7) = some overlap
           - Use: Find entities with similar lifestyles/behaviors
           
        2. TRAJECTORY (64-dim): Encodes HOW entities MOVE
           - Captures route patterns independent of specific locations
           - Use: Find entities with similar commute/travel patterns
           
        3. DWELL LOCATION (64-dim): Encodes behavioral profile of PLACES
           - Captures: visit times, duration, visitor diversity, place type, regularity
           - Similar locations = places visited at similar times by similar-profile visitors
           - Use: Find places with similar usage patterns (e.g., "coffee shops like this one")
        
        All use cosine similarity via pgvector: 0.0 = different, 1.0 = identical
        
        ANOMALY DETECTION:
        - Uses LSTM autoencoder to learn normal daily patterns for each entity
        - Flags days with high reconstruction error as anomalous
        - Anomaly score typically 0-1, higher = more unusual
        - Triggers: unusual times, unexpected locations, communication changes, missing activity
        - Train models first, then detect anomalies over a date range`,
    };
  };

  // Handle ask
  const handleAsk = async () => {
    if (!question.trim() || isLoading) return;

    const queryText = question.trim();
    setIsLoading(true);
    setIsChatMode(true);
    setQuestion('');

    // Add user message
    setChatHistory(prev => [...prev, {
      role: 'user',
      content: queryText,
      timestamp: new Date(),
    }]);

    // Check for simple action-only commands (don't need full AI response)
    const simpleCommandActions = parseActionsFromResponse('', queryText);
    const isSimpleCommand = simpleCommandActions.some(a => 
      a.autoExecute && ['zoom_to_node', 'highlight_nodes', 'filter_entities', 'expand_node', 'clear_highlights'].includes(a.type)
    );
    
    if (isSimpleCommand && simpleCommandActions.length > 0) {
      // Execute actions immediately without waiting for AI
      const executedLabels: string[] = [];
      simpleCommandActions.filter(a => a.autoExecute).forEach(action => {
        console.log('[LinkAnalysisSentinel] Quick-executing action:', action);
        executeAction(action);
        executedLabels.push(action.label || action.type);
      });
      
      // Add simple confirmation message
      setChatHistory(prev => [...prev, {
        role: 'assistant',
        content: `✅ Done! ${executedLabels.join(', ')}`,
        timestamp: new Date(),
      }]);
      
      setIsLoading(false);
      return; // Skip the full AI query
    }

    try {
      const context = buildContext();

      const response = await fetch('/api/link-analysis-chat/ask', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
        },
        body: JSON.stringify({
          question: queryText,
          context,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const answer = data.answer || 'No response received.';
        
        // Parse for actionable insights
        const actions = parseActionsFromResponse(answer, queryText);
        
        // Auto-execute actions marked for auto-execution
        const autoActions = actions.filter(a => a.autoExecute);
        const manualActions = actions.filter(a => !a.autoExecute);
        
        // Execute auto actions (like filtering the graph, filtering entities)
        let autoFilterApplied = '';
        let entitiesFiltered = 0;
        let nodesHighlighted = 0;
        autoActions.forEach(action => {
          console.log('[LinkAnalysisSentinel] Auto-executing action:', action);
          executeAction(action);
          if (action.type === 'filter_connection_type' && action.connectionType) {
            autoFilterApplied = action.connectionType;
          }
          if (action.type === 'filter_entities' && action.nodeIds) {
            entitiesFiltered = action.nodeIds.length;
          }
          if (action.type === 'highlight_nodes' && action.nodeIds) {
            nodesHighlighted = action.nodeIds.length;
          }
        });
        
        // If we auto-filtered by connection type, add a "reset filter" button
        if (autoFilterApplied) {
          manualActions.push({
            type: 'filter_connection_type',
            connectionType: 'digital',
            label: '🔄 Show all Digital POL',
            icon: <ClearIcon fontSize="small" />,
          });
        }
        
        // If we filtered to specific entities, add a "clear entity filter" button
        if (entitiesFiltered > 0) {
          manualActions.push({
            type: 'reset_view',
            label: '🔄 Show all entities',
            icon: <ClearIcon fontSize="small" />,
          });
        }
        
        // If we highlighted nodes, add a clear highlights button
        if (nodesHighlighted > 0) {
          manualActions.push({
            type: 'clear_highlights',
            label: '🧹 Clear highlights',
            icon: <ClearIcon fontSize="small" />,
          });
        }
        
        // Build auto-action notice
        let autoNotice = '';
        if (autoFilterApplied) {
          autoNotice += `\n\n*📊 Graph filtered to **${autoFilterApplied.toUpperCase()}** connections*`;
        }
        if (entitiesFiltered > 0) {
          autoNotice += `\n\n*🎯 Graph filtered to **${entitiesFiltered} entities** mentioned in analysis*`;
        }
        if (nodesHighlighted > 0) {
          autoNotice += `\n\n*✨ **${nodesHighlighted} nodes** highlighted on graph*`;
        }

        setChatHistory(prev => [...prev, {
          role: 'assistant',
          content: answer + autoNotice,
          timestamp: new Date(),
          actions: manualActions,
        }]);
      } else {
        setChatHistory(prev => [...prev, {
          role: 'assistant',
          content: '❌ Failed to get a response. Please try again.',
          timestamp: new Date(),
        }]);
      }
    } catch (error) {
      console.error('Link Analysis Sentinel error:', error);
      setChatHistory(prev => [...prev, {
        role: 'assistant',
        content: '❌ Connection error. Please check your network.',
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: colors.background,
      overflow: 'hidden',
    }}>
      {/* Header - Smart Analytics */}
      <Box sx={{
        p: 1.5,
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}>
        <Box sx={{
          width: 28,
          height: 28,
          borderRadius: '6px',
          background: `linear-gradient(135deg, rgba(${colors.accentRgb}, 0.2), rgba(${colors.accentRgb}, 0.1))`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid rgba(${colors.accentRgb}, 0.3)`,
        }}>
          <NetworkIcon sx={{ color: colors.accent, fontSize: '1rem' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: colors.text, fontWeight: 600, fontSize: '0.85rem' }}>
            Smart Analytics
          </Typography>
          <Typography sx={{ color: colors.textSecondary, fontSize: '0.65rem' }}>
            {isFilteredView ? (
              <span style={{ color: '#f59e0b' }}>
                🔍 {networkStats.totalNodes} / {totalNodesInDataset || '?'} nodes • {networkStats.totalLinks} / {totalLinksInDataset || '?'} connections (filtered)
              </span>
            ) : (
              <>{networkStats.totalNodes} nodes • {networkStats.totalLinks} connections</>
            )}
          </Typography>
        </Box>
        
        {highlightedNodes.size > 0 && (
          <Chip
            label={`✨ ${highlightedNodes.size}`}
            size="small"
            onDelete={() => executeAction({ type: 'clear_highlights', label: 'Clear' })}
            sx={{
              height: 20,
              fontSize: '0.65rem',
              bgcolor: 'rgba(245, 158, 11, 0.2)',
              color: '#f59e0b',
            }}
          />
        )}
      </Box>

      {/* Ask AI Section - At top for easy access */}
      <Box sx={{ 
        p: 1.5, 
        bgcolor: 'transparent',
        borderBottom: `1px solid ${colors.border}`,
      }}>
        {/* Ask Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AskIcon sx={{ color: colors.accent, fontSize: '1rem' }} />
          <Typography sx={{ color: colors.text, fontWeight: 600, fontSize: '0.8rem' }}>
            Ask Sentinel
          </Typography>
          
          {/* Chat history indicator */}
          {chatHistory.length > 0 && (
            <Chip
              label={`💬 ${chatHistory.length}`}
              size="small"
              onClick={() => setIsChatMode(!isChatMode)}
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: isChatMode ? colors.accent : `rgba(${colors.accentRgb}, 0.15)`,
                color: isChatMode ? 'white' : colors.accent,
                cursor: 'pointer',
                '&:hover': { bgcolor: isChatMode ? colors.accent : `rgba(${colors.accentRgb}, 0.25)` },
              }}
            />
          )}
          
          {chatHistory.length > 0 && (
            <>
              <Tooltip title="Export to PDF (with map/graph)">
                <IconButton
                  size="small"
                  onClick={async () => {
                    await generateSentinelChatPDF(
                      {
                        title: 'Link Analysis Report',
                        subtitle: 'Network Intelligence Conversation',
                        messages: chatHistory.map(m => ({
                          role: m.role,
                          content: m.content,
                          timestamp: m.timestamp,
                        })),
                        additionalContext: `Analysis Context: ${nodes.length} nodes, ${links.length} connections analyzed`,
                        classification: 'Controlled Unclassified Information (CUI)',
                      },
                      'Link_Analysis_Report.pdf',
                      { graphElementId: 'network-graph-container', mapElementId: 'network-map-container' }
                    );
                  }}
                  sx={{ ml: 'auto', color: colors.textSecondary, p: 0.25 }}
                >
                  <PdfIcon sx={{ fontSize: '0.9rem' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear chat">
                <IconButton
                  size="small"
                  onClick={() => { setChatHistory([]); setIsChatMode(false); setHighlightedNodes(new Set()); }}
                  sx={{ color: colors.textSecondary, p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: '0.9rem' }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>

        {/* Question Input */}
        <TextField
          fullWidth
          size="small"
          placeholder="Ask about connections, patterns, SMS conversations..."
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !isLoading) {
              handleAsk();
            }
          }}
          disabled={isLoading}
          InputProps={{
            sx: {
              bgcolor: isDark ? 'rgba(20, 22, 35, 0.8)' : 'rgba(255, 255, 255, 0.9)',
              color: colors.text,
              fontSize: '0.8rem',
              '& fieldset': { borderColor: colors.border },
              '&:hover fieldset': { borderColor: colors.accent },
              '&.Mui-focused fieldset': { borderColor: colors.accent },
            },
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title="Prompt Library">
                  <IconButton
                    size="small"
                    onClick={(e) => setPromptMenuAnchor(e.currentTarget)}
                    sx={{
                      color: promptMenuOpen ? colors.accent : colors.textSecondary,
                      '&:hover': { color: colors.accent },
                    }}
                  >
                    <PromptIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={handleAsk}
                  disabled={isLoading || !question.trim()}
                  sx={{ color: colors.accent }}
                >
                  {isLoading ? <CircularProgress size={16} /> : <SendIcon sx={{ fontSize: '1rem' }} />}
                </IconButton>
              </Box>
            ),
          }}
        />

        {/* Prompt Library Menu */}
        <Menu
          anchorEl={promptMenuAnchor}
          open={promptMenuOpen}
          onClose={() => setPromptMenuAnchor(null)}
          PaperProps={{
            sx: {
              bgcolor: isDark ? '#1a1f2e' : '#ffffff',
              border: `1px solid ${colors.border}`,
              maxHeight: 400,
              minWidth: 280,
              boxShadow: isDark 
                ? '0 8px 32px rgba(0, 0, 0, 0.5)' 
                : '0 8px 32px rgba(0, 0, 0, 0.15)',
            },
          }}
        >
          {predefinedPrompts.map((category, catIdx) => (
            <Box key={catIdx}>
              <MenuItem disabled sx={{ opacity: 1 }}>
                <ListItemIcon sx={{ color: colors.accent, minWidth: 32 }}>
                  {category.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={category.category} 
                  primaryTypographyProps={{ fontSize: '0.75rem', fontWeight: 600, color: colors.text }}
                />
              </MenuItem>
              {category.prompts.map((prompt, promptIdx) => (
                <MenuItem
                  key={promptIdx}
                  onClick={() => {
                    setQuestion(prompt.prompt);
                    setPromptMenuAnchor(null);
                    setTimeout(() => handleAsk(), 100);
                  }}
                  sx={{ pl: 4, py: 0.5 }}
                >
                  <ListItemText 
                    primary={prompt.label}
                    primaryTypographyProps={{ fontSize: '0.7rem', color: colors.textSecondary }}
                  />
                </MenuItem>
              ))}
            </Box>
          ))}
        </Menu>
      </Box>

      {/* Chat History - shows when there are messages and chat mode is on */}
      {isChatMode && chatHistory.length > 0 && (
        <Box sx={{
          flex: 1,           // Fill available vertical space
          minHeight: 0,      // Allow shrinking below content size (enables scroll)
          overflowY: 'auto',
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          borderBottom: `1px solid ${colors.border}`,
        }}>
          {chatHistory.map((msg, idx) => (
            <Box key={idx}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: msg.role === 'user'
                    ? `rgba(${colors.accentRgb}, 0.1)`
                    : colors.cardBg,
                  border: `1px solid ${msg.role === 'user' ? colors.accent : colors.border}`,
                  borderLeft: msg.role === 'user' ? `3px solid ${colors.accent}` : undefined,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography sx={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: msg.role === 'user' ? colors.accent : colors.text,
                  }}>
                    {msg.role === 'user' ? '👤 You' : '🛡️ Sentinel'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: colors.textTertiary }}>
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    color: colors.text,
                    whiteSpace: 'pre-wrap',
                    '& strong': { fontWeight: 600 },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: msg.content
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\n/g, '<br/>')
                  }}
                />
              </Box>
              
              {/* Action buttons for AI responses */}
              {msg.role === 'assistant' && msg.actions && msg.actions.length > 0 && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {msg.actions.map((action, actionIdx) => (
                    <Button
                      key={actionIdx}
                      size="small"
                      variant="outlined"
                      startIcon={action.icon}
                      onClick={() => executeAction(action)}
                      sx={{
                        fontSize: '0.65rem',
                        py: 0.25,
                        px: 1,
                        textTransform: 'none',
                        borderColor: colors.accent,
                        color: colors.accent,
                        '&:hover': {
                          bgcolor: `rgba(${colors.accentRgb}, 0.1)`,
                          borderColor: colors.accent,
                        },
                      }}
                    >
                      {action.label}
                    </Button>
                  ))}
                </Stack>
              )}
            </Box>
          ))}

          {isLoading && (
            <Box sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: colors.cardBg,
              border: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>
              <CircularProgress size={14} />
              <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>
                Analyzing network...
              </Typography>
            </Box>
          )}

          <div ref={chatEndRef} />
        </Box>
      )}

      {/* Quick Stats (when not in chat mode) */}
      {!isChatMode && (
        <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
          <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary, mb: 1 }}>
            Network Overview
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip
              icon={<NetworkIcon sx={{ fontSize: '0.9rem' }} />}
              label={`${networkStats.deviceCount} known`}
              size="small"
              onClick={() => executeAction({
                type: 'filter_entities',
                nodeIds: nodes.filter(n => n.type === 'device').map(n => n.id),
                label: 'Filter to devices',
              })}
              sx={{ fontSize: '0.7rem', bgcolor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', cursor: 'pointer' }}
            />
            <Chip
              icon={<LocationIcon sx={{ fontSize: '0.9rem' }} />}
              label={`${networkStats.locationCount} place`}
              size="small"
              onClick={() => executeAction({
                type: 'show_on_map',
                nodeIds: nodes.filter(n => n.type === 'location').map(n => n.id),
                label: 'Show locations',
              })}
              sx={{ fontSize: '0.7rem', bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', cursor: 'pointer' }}
            />
            <Chip
              icon={<PhoneIcon sx={{ fontSize: '0.9rem' }} />}
              label={`${networkStats.phoneCount} external`}
              size="small"
              onClick={() => executeAction({
                type: 'highlight_nodes',
                nodeIds: nodes.filter(n => n.type === 'phone').map(n => n.id),
                label: 'Highlight phones',
              })}
              sx={{ fontSize: '0.7rem', bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', cursor: 'pointer' }}
            />
            {networkStats.appCount > 0 && (
              <Chip
                icon={<AppsIcon sx={{ fontSize: '0.9rem' }} />}
                label={`${networkStats.appCount} apps`}
                size="small"
                onClick={() => executeAction({
                  type: 'filter_connection_type',
                  connectionType: 'app',
                  label: 'Filter to apps',
                })}
                sx={{ fontSize: '0.7rem', bgcolor: 'rgba(103, 58, 183, 0.1)', color: '#673ab7', cursor: 'pointer' }}
              />
            )}
          </Box>

          <Typography sx={{ fontSize: '0.7rem', color: colors.textTertiary, mb: 0.5 }}>
            Connection Types (click to filter):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {Object.entries(networkStats.connectionTypes).map(([type, count]) => (
              <Chip
                key={type}
                label={`${type}: ${count}`}
                size="small"
                variant="outlined"
                onClick={() => executeAction({
                  type: 'filter_connection_type',
                  connectionType: type,
                  label: `Filter to ${type}`,
                })}
                sx={{ fontSize: '0.6rem', height: 18, cursor: 'pointer' }}
              />
            ))}
          </Box>

          {networkStats.topConnected.length > 0 && (
            <>
              <Typography sx={{ fontSize: '0.7rem', color: colors.textTertiary, mb: 0.5 }}>
                Most Connected (click to focus):
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {networkStats.topConnected.slice(0, 5).map(([nodeId, count]) => {
                  const node = nodes.find(n => n.id === nodeId);
                  const isHighlighted = highlightedNodes.has(nodeId);
                  return (
                    <Box
                      key={nodeId}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 0.5,
                        borderRadius: 1,
                        bgcolor: isHighlighted ? `rgba(${colors.accentRgb}, 0.15)` : colors.cardBg,
                        border: isHighlighted ? `1px solid ${colors.accent}` : '1px solid transparent',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: `rgba(${colors.accentRgb}, 0.1)` },
                      }}
                      onClick={() => {
                        executeAction({
                          type: 'zoom_to_node',
                          nodeIds: [nodeId],
                          label: `Focus ${node?.label || nodeId}`,
                        });
                        onNodeSelect?.(nodeId);
                      }}
                    >
                      <Typography sx={{ fontSize: '0.7rem', color: colors.text }}>
                        {node?.label || nodeId}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          label={count}
                          size="small"
                          sx={{ height: 16, fontSize: '0.6rem', minWidth: 24 }}
                        />
                        <Tooltip title="Filter to this entity">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              executeAction({
                                type: 'filter_entities',
                                nodeIds: [nodeId],
                                label: `Filter to ${node?.label || nodeId}`,
                              });
                            }}
                            sx={{ p: 0.25, color: colors.textSecondary }}
                          >
                            <FilterIcon sx={{ fontSize: '0.8rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Chat End Ref */}
      <Box ref={chatEndRef} />
    </Box>
  );
};

export default LinkAnalysisSentinel;
